import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Send } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CollaborationSection = ({ data }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to send message. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="collaborate" className="py-24 md:py-32 bg-gradient-to-br from-amber-500 via-rose-500 to-pink-500 text-white">
      <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            {data.title}
          </h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed">
            {data.description}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-12">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {data.opportunities.map((opportunity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.05 }}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center font-medium hover:bg-white/30 transition-colors cursor-default">
                {opportunity}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Email Contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-white dark:bg-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl text-center">
          <Mail className="w-16 h-16 text-amber-600 dark:text-amber-400 mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Get in Touch
          </h3>
          <a 
            href="mailto:vandana@thehappylab.in"
            className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600 dark:text-amber-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors inline-block break-all">
            vandana@thehappylab.in
          </a>
        </motion.div>
      </div>
    </section>
  );
};